import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { format } from 'date-fns';
import { GET_COMPANY_HOLIDAYS } from '../../graphql/team-availability-queries';
import { UPSERT_COMPANY_HOLIDAY, DELETE_COMPANY_HOLIDAY } from '../../graphql/team-availability-mutations';
import { toast } from 'react-hot-toast';
import HolidayFormModal from './HolidayFormModal';
import { toLocalCalendarDate } from '../../utils/availabilityDateUtils';

function HolidayManagementView() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);

  const currentYear = new Date().getFullYear();
  
  const { data, loading, refetch } = useQuery(GET_COMPANY_HOLIDAYS, {
    variables: { year: currentYear },
    fetchPolicy: 'network-only'
  });

  const [deleteHoliday] = useMutation(DELETE_COMPANY_HOLIDAY, {
    onCompleted: () => {
      toast.success('Holiday deleted successfully');
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete holiday');
    }
  });

  const holidays = data?.companyHolidays || [];

  const handleAdd = () => {
    setEditingHoliday(null);
    setIsModalOpen(true);
  };

  const handleEdit = (holiday) => {
    setEditingHoliday(holiday);
    setIsModalOpen(true);
  };

  const handleDelete = (holidayId) => {
    if (window.confirm('Are you sure you want to delete this holiday?')) {
      deleteHoliday({ variables: { id: holidayId } });
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingHoliday(null);
  };

  const handleSaved = () => {
    refetch();
    handleCloseModal();
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Loading...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Company Holidays</h2>
          <button
            onClick={handleAdd}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-sky-600 hover:bg-sky-700"
          >
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Holiday
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {holidays.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                    No holidays found. Click "Add Holiday" to create one.
                  </td>
                </tr>
              ) : (
                holidays.map((holiday) => (
                  <tr key={holiday.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {holiday.name}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {format(toLocalCalendarDate(holiday.date), 'MMMM d, yyyy')}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {holiday.description || '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleEdit(holiday)}
                        className="text-sky-600 hover:text-sky-900 mr-4"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(holiday.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && (
        <HolidayFormModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          holiday={editingHoliday}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

export default HolidayManagementView;

